class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  /* Instance id from the Global group JSON key (e.g. "cart-drawer"), not the
     section type filename. Cart AJAX must request this id so Shopify returns
     the merchant-configured Plus Ultra drawer (blocks/settings), not a blank
     default render of the section type. */
  static getDrawerSectionId() {
    const sectionEl = document.querySelector('cart-drawer')?.closest('[id^="shopify-section-"]');
    if (sectionEl?.id) return sectionEl.id.replace(/^shopify-section-/, '');
    return 'cart-drawer';
  }

  setHeaderCartIconAccessibility() {
    /* Plus Ultra headers/toolbars open the drawer via [data-hdt-cart-icon]
       delegation in header-plus-ultra.js — skip Dawn's #cart-icon-bubble bind
       so we don't fight that wiring. */
    if (document.querySelector('[data-hdt-cart-icon]')) return;

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // cart/add.js often omits item_count; treat a successful add as non-empty.
    const isEmpty = parsedState.item_count === 0;
    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
    this.productId = parsedState.id;

    const sections = this.getSectionsToRender();
    const missingSection = sections.some((section) => !parsedState.sections?.[section.id]);

    const finishOpen = () => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    };

    if (missingSection) {
      // Fallback when /cart/add omitted section HTML (or wrong section id).
      const sectionId = CartDrawer.getDrawerSectionId();
      fetch(`${routes.cart_url}?section_id=${sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const source = html.querySelector('#CartDrawer');
          const target = document.querySelector('#CartDrawer');
          if (source && target) target.innerHTML = source.innerHTML;
          this.classList.toggle('is-empty', false);
          this.querySelector('.drawer__inner')?.classList.toggle('is-empty', false);
        })
        .catch((e) => console.error(e))
        .finally(() => setTimeout(finishOpen));
      return;
    }

    sections.forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const html = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (html == null) return;
      sectionElement.innerHTML = html;
    });

    setTimeout(finishOpen);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: CartDrawer.getDrawerSectionId(),
        selector: '#CartDrawer',
      },
      /* cart-icon-bubble intentionally omitted: replacing #cart-icon-bubble
         with Dawn’s cart-icon-bubble section swaps the Plus Ultra polaris
         icon for the default. Header/toolbar counts update via
         header-plus-ultra.js on PUB_SUB_EVENTS.cartUpdate. */
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: CartDrawer.getDrawerSectionId(),
        selector: '.drawer__inner',
      },
      /* Skip cart-icon-bubble — Plus Ultra icons keep their markup; counts
         refresh through header-plus-ultra.js cart-update subscription. */
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
